'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { useRouter, useSearchParams } from 'next/navigation';
import {
  LayoutTemplate,
  Pencil,
  Power,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';

import AccessDeniedModal from '@/components/access-denied-modal';
import AddConnectorModal from '@/components/add-connector-modal';
import ConnectorBanner from '@/components/connector-banner';
import ConnectorRowActions from '@/components/connector-row-actions';
import {
  CONNECTOR_DASHBOARD_TABS_LIST_CLASS,
  CONNECTOR_DASHBOARD_TABS_TRIGGER_CLASS,
} from '@/components/connector-tab-styles';
import ConfirmDeleteModal from '@/components/system-delete-modal';
import FilterDropdown from '@/components/filter-dropdown';
import AddMcpModal from '@/components/mcp/add-mcp-modal';
import Pagination from '@/components/pagination';
import PageLoadingState from '@/components/page-loading-state';
import { Button, Input, Tabs, TabsList, TabsTrigger } from '@/components/ui';
import { InlineStatus } from '@/components/ui/inline-status';
import { Toaster, useToast } from '@/components/ui/toaster';
import { useConnectorAccess } from '@/hooks/use-connector-access';
import { useDebounce } from '@/hooks/use-debounce';
import {
  CONNECTOR_HOME_SCREEN,
  MCP_HOME_SCREEN,
  SPECS_ACTIONS_SCREEN,
} from '@/lib/connector-access';
import {
  buildWorkspaceAwarePath,
  useActiveWorkspaceScopeKey,
} from '@/lib/active-workspace';
import { coerceLocale } from '@/i18n/locales';
import { withLocalePath } from '@/lib/locale-routing';
import { deriveMcpLifecycle, isInactive } from '@/lib/mcp-status';
import { pushWithMinimumDelay } from '@/lib/navigation';
import { CONNECTOR_VIEW_PARAMS as VIEW_PARAMS } from '@/lib/connector-view-params';
import { deriveSystemLifecycle, isSystemInactive } from '@/lib/system-lifecycle';
import { domId } from '@/lib/dom-id';
import {
  useDeleteMcp,
  useMcps,
  useSetMcpStatus,
} from '@/services/hooks/use-mcps';
import {
  useDeleteSystem,
  useSetSystemStatus,
  useSystems,
} from '@/services/hooks/use-systems';
import type { MCP } from '@/services/schemas/mcps-schemas';
import type { SystemRecord } from '@/services/system-service';

type ConnectorTab = 'systems' | 'mcp';
type ViewParam = keyof typeof VIEW_PARAMS;

const STATUS_FILTERS = ['all', 'active', 'inactive'] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];
const AUTH_FILTERS = [
  'all',
  'api_key',
  'basic_auth',
  'bearer_token',
  'oauth2',
  'none',
  'missing_credentials',
] as const;
type AuthFilter = (typeof AUTH_FILTERS)[number];
const SORT_OPTIONS = [
  { value: 'updated', labelKey: 'sort.recentlyUpdated' },
  { value: 'name-asc', labelKey: 'sort.nameAsc' },
  { value: 'name-desc', labelKey: 'sort.nameDesc' },
  { value: 'count-desc', labelKey: 'sort.mostConfigured' },
  { value: 'count-asc', labelKey: 'sort.fewestConfigured' },
] as const;
type SortValue = (typeof SORT_OPTIONS)[number]['value'];
const SORT_VALUES: readonly SortValue[] = SORT_OPTIONS.map(option => option.value);
const DEFAULT_VIEW = { status: 'all', auth: 'all', sort: 'updated' } as const;

function pickOption<T extends string>(value: string | null, options: readonly T[]): T | undefined {
  return options.find(option => option === value);
}

type DeleteTarget =
  | { kind: 'system'; id: string; name: string }
  | { kind: 'mcp'; id: string; name: string };

const PAGE_SIZE_OPTIONS = [10, 20, 50];
// Row names open the detail page, so they read as links (like prd's clickable
// connector cards): pointer cursor plus a link hover state.
const DETAIL_LINK_NAME_CLASS = 'group-hover:text-[#713D98] group-hover:underline';

function formatUpdated(value: string | undefined, locale: string): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
}

function connectorInitials(name: string | undefined, fallback: string): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return fallback;
  return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? parts[0]?.[1] ?? ''}`.toUpperCase();
}

function LifecycleBadge({
  className,
  dotClassName,
  label,
}: {
  className: string;
  dotClassName: string;
  label: string;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${className}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dotClassName}`} aria-hidden="true" />
      {label}
    </span>
  );
}

function MetricCard({ label, value, testId }: { label: string; value: number; testId: string }) {
  return (
    <div data-testid={testId} className="rounded-xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">{value}</p>
    </div>
  );
}

function LoadingRows({ columns }: { columns: number }) {
  return (
    <>
      {Array.from({ length: 5 }).map((_, index) => (
        <tr key={index} className="border-b border-slate-100 last:border-0">
          {Array.from({ length: columns }).map((__, column) => (
            <td key={column} className="px-4 py-4">
              <span className="block h-4 animate-pulse rounded bg-slate-100" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
}

export default function ConnectorManagementPage({
  initialTab,
  showBanner = true,
  renderDialogs = true,
  onAddSystem,
  onAddMcp,
}: {
  initialTab: ConnectorTab;
  showBanner?: boolean;
  renderDialogs?: boolean;
  onAddSystem?: () => void;
  onAddMcp?: () => void;
}) {
  const router = useRouter();
  const locale = coerceLocale(useLocale());
  const tListing = useTranslations('ConnectorListing');
  const tHome = useTranslations('Home');
  const tStatus = useTranslations('StatusFilter');
  const tMcp = useTranslations('Mcp');
  const tMcpAuth = useTranslations('Mcp.authMethod');
  const tSystem = useTranslations('SystemActionsPage');
  const tSystemCard = useTranslations('SystemCard');
  const tDelete = useTranslations('SystemDeleteModal');
  const tError = useTranslations('ErrorBoundary');
  const workspaceScope = useActiveWorkspaceScopeKey();
  const {
    canViewSystems,
    canCreateSystem,
    canUpdateSystem,
    canDeleteSystem,
    canViewMcps,
    canCreateMcp,
    canUpdateMcp,
    canManageMcpStatus,
    canDeleteMcp,
    canAccessScreen,
    isLoading: isAccessLoading,
  } = useConnectorAccess();

  const searchParams = useSearchParams();
  // Tab, filters and sort are read from the URL so a reload keeps the view.
  const viewParam = (name: ViewParam) => searchParams.get(VIEW_PARAMS[name]);
  const statusFilter: StatusFilter = pickOption(viewParam('status'), STATUS_FILTERS) ?? DEFAULT_VIEW.status;
  const authFilter: AuthFilter = pickOption(viewParam('auth'), AUTH_FILTERS) ?? DEFAULT_VIEW.auth;
  const sortValue: SortValue = pickOption(viewParam('sort'), SORT_VALUES) ?? DEFAULT_VIEW.sort;
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  // A cleared search applies at once; typing waits for the debounce.
  const querySearch = search.trim() ? debouncedSearch : '';
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [settledTotal, setSettledTotal] = useState(0);
  const [addSystemOpen, setAddSystemOpen] = useState(false);
  const [addMcpOpen, setAddMcpOpen] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const { toasts, addToast, removeToast } = useToast();

  const visibleTabs = ([
    canViewSystems ? 'systems' : null,
    canViewMcps ? 'mcp' : null,
  ] as const).filter((tab): tab is ConnectorTab => tab !== null);
  // The route's own tab, unless this user cannot see it.
  const defaultTab = visibleTabs.includes(initialTab) ? initialTab : (visibleTabs[0] ?? initialTab);
  const activeTab = pickOption(viewParam('tab'), visibleTabs) ?? defaultTab;
  const canViewCurrent = activeTab === 'systems' ? canViewSystems : canViewMcps;
  const currentScreen = activeTab === 'systems' ? CONNECTOR_HOME_SCREEN : MCP_HOME_SCREEN;
  const canOpenSystemDetails = canViewSystems && canAccessScreen(SPECS_ACTIONS_SCREEN);
  const canEditSystem = canOpenSystemDetails && canUpdateSystem;
  const canOpenCurrent = canViewCurrent && canAccessScreen(currentScreen);

  // Each tab of each workspace opens on page 1 with an empty search. Adjusting
  // the state while rendering, rather than in an effect, keeps the previous
  // view's page and search out of the new view's first request.
  const viewKey = `${workspaceScope}:${activeTab}`;
  const [shownViewKey, setShownViewKey] = useState(viewKey);
  if (shownViewKey !== viewKey) {
    setShownViewKey(viewKey);
    setSearch('');
    setPage(1);
    setSettledTotal(0);
  }

  // A value equal to the default is dropped so the default view keeps a clean URL.
  const replaceViewParams = useCallback(
    (changes: Partial<Record<ViewParam, string | null>>) => {
      const params = new URLSearchParams(window.location.search);
      for (const [name, value] of Object.entries(changes)) {
        const key = VIEW_PARAMS[name as ViewParam];
        if (value) params.set(key, value);
        else params.delete(key);
      }
      const query = params.toString();
      // null state: Next.js then keeps its own history entry data and syncs
      // useSearchParams (a state carrying its internal marker is skipped).
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
      );
    },
    [],
  );
  const setViewOption = (name: keyof typeof DEFAULT_VIEW, value: string) => {
    replaceViewParams({ [name]: value === DEFAULT_VIEW[name] ? null : value });
  };

  useEffect(() => {
    setPage(1);
  }, [querySearch, statusFilter, authFilter, sortValue, pageSize]);

  const systemSort = useMemo(() => {
    switch (sortValue) {
      case 'name-asc':
        return { field: 'name', order: 'asc' } as const;
      case 'name-desc':
        return { field: 'name', order: 'desc' } as const;
      case 'count-desc':
        return { field: 'api_count', order: 'desc' } as const;
      case 'count-asc':
        return { field: 'api_count', order: 'asc' } as const;
      default:
        return { field: 'last_updated', order: 'desc' } as const;
    }
  }, [sortValue]);

  const mcpSort = useMemo((): Record<string, 1 | -1> => {
    switch (sortValue) {
      case 'name-asc':
        return { name: 1 };
      case 'name-desc':
        return { name: -1 };
      case 'count-desc':
        return { total_tool: -1 };
      case 'count-asc':
        return { total_tool: 1 };
      default:
        return { last_updated_time: -1 };
    }
  }, [sortValue]);

  const systemsResult = useSystems({
    enabled: activeTab === 'systems' && canOpenCurrent && !isAccessLoading,
    search: querySearch,
    lifecycleFilter: statusFilter,
    authFilter,
    sortField: systemSort.field,
    sortOrder: systemSort.order,
    limit: pageSize,
    offset: (page - 1) * pageSize,
  });
  const mcpsResult = useMcps({
    enabled: activeTab === 'mcp' && canOpenCurrent && !isAccessLoading,
    includeTotal: true,
    search: querySearch,
    filter: statusFilter === 'all' ? undefined : { status: statusFilter },
    sort: mcpSort,
    limit: pageSize,
    offset: (page - 1) * pageSize,
  });

  const totalSystemsResult = useSystems({
    enabled: canViewSystems && !isAccessLoading,
    limit: 1,
    offset: 0,
    sortField: 'last_updated',
    sortOrder: 'desc',
  });
  const activeSystemsResult = useSystems({
    enabled: canViewSystems && !isAccessLoading,
    lifecycleFilter: 'active',
    limit: 1,
    offset: 0,
    sortField: 'last_updated',
    sortOrder: 'desc',
  });
  const totalMcpsResult = useMcps({
    enabled: canViewMcps && !isAccessLoading,
    includeTotal: true,
    limit: 1,
    offset: 0,
    sort: { last_updated_time: -1 },
  });
  const activeMcpsResult = useMcps({
    enabled: canViewMcps && !isAccessLoading,
    includeTotal: true,
    filter: { status: 'active' },
    limit: 1,
    offset: 0,
    sort: { last_updated_time: -1 },
  });

  const systems = systemsResult.systems ?? [];
  const mcps = mcpsResult.mcps ?? [];
  const rows = activeTab === 'systems' ? systems : mcps;
  const loading = activeTab === 'systems' ? systemsResult.isLoading : mcpsResult.isLoading;
  const error = activeTab === 'systems' ? systemsResult.error : mcpsResult.error;
  // The server's total for the current query; unknown while that page loads.
  const loadedTotal = activeTab === 'systems' ? systemsResult.total : mcpsResult.total;
  useEffect(() => {
    if (loadedTotal !== undefined) setSettledTotal(loadedTotal);
  }, [loadedTotal]);
  // While a page loads, the last total keeps the pagination bar and the results
  // count in place instead of dropping them to 0.
  const total = loadedTotal ?? (loading ? settledTotal : rows.length);

  // Clamp only against a loaded total: a placeholder 0 sent the first visit to
  // page 2 straight back to page 1.
  useEffect(() => {
    if (loadedTotal === undefined) return;
    const totalPages = Math.max(1, Math.ceil(loadedTotal / pageSize));
    if (page > totalPages) setPage(totalPages);
  }, [loadedTotal, page, pageSize]);

  const refreshMcps = useCallback(async () => {
    await Promise.allSettled([
      mcpsResult.mutate(undefined, { revalidate: true, throwOnError: true }),
      totalMcpsResult.mutate(undefined, { revalidate: true, throwOnError: true }),
      activeMcpsResult.mutate(undefined, { revalidate: true, throwOnError: true }),
    ]);
  }, [activeMcpsResult, mcpsResult, totalMcpsResult]);

  const setSystemStatus = useSetSystemStatus();
  const deleteSystem = useDeleteSystem();
  const setMcpStatus = useSetMcpStatus();
  const deleteMcp = useDeleteMcp();

  const navigateToSystem = useCallback((system: SystemRecord, edit = false) => {
    if (!canOpenSystemDetails) return;
    const params = new URLSearchParams({ system_id: system.system_id });
    if (system.name) params.set('name', system.name);
    if (system.description) params.set('description', system.description);
    if (edit) params.set('edit', '1');
    void pushWithMinimumDelay(
      router,
      buildWorkspaceAwarePath(withLocalePath(locale, '/specs/actions'), params),
    );
  }, [canOpenSystemDetails, locale, router]);

  const navigateToMcp = useCallback((server: MCP, edit = false) => {
    const params = edit ? new URLSearchParams({ edit: '1' }) : undefined;
    void pushWithMinimumDelay(
      router,
      buildWorkspaceAwarePath(withLocalePath(locale, `/mcp/${server.mcp_id}`), params),
    );
  }, [locale, router]);

  const switchTab = (tab: ConnectorTab) => {
    if (tab === activeTab) return;
    // Each tab opens on its default view.
    replaceViewParams({ tab: tab === defaultTab ? null : tab, status: null, auth: null, sort: null });
  };

  const handleSystemLifecycle = async (system: SystemRecord) => {
    if (!canUpdateSystem || system.read_only || busyId) return;
    setBusyId(system.system_id);
    setMutationError(null);
    const activating = isSystemInactive(system);
    try {
      await setSystemStatus(system.system_id, activating ? 'active' : 'inactive');
      addToast({
        description: tListing(activating ? 'toast.activated' : 'toast.deactivated', { name: system.name }),
        variant: 'success',
      });
    } catch (caught) {
      setMutationError(caught instanceof Error ? caught.message : tListing('lifecycleError'));
    } finally {
      setBusyId(null);
    }
  };

  const handleMcpLifecycle = async (server: MCP) => {
    if (!canManageMcpStatus || busyId) return;
    setBusyId(server.mcp_id);
    setMutationError(null);
    const activating = isInactive(server);
    try {
      await setMcpStatus(server.mcp_id, activating ? 'active' : 'inactive');
      addToast({
        description: tListing(activating ? 'toast.mcpActivated' : 'toast.mcpDeactivated', {
          name: server.name || tMcp('untitled'),
        }),
        variant: 'success',
      });
    } catch (caught) {
      setMutationError(caught instanceof Error ? caught.message : tListing('lifecycleError'));
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget || busyId) return;
    setBusyId(deleteTarget.id);
    setDeleteError(null);
    try {
      if (deleteTarget.kind === 'system') {
        await deleteSystem(deleteTarget.id);
      } else {
        await deleteMcp(deleteTarget.id);
      }
      addToast({
        description: tListing('toast.deleted', { name: deleteTarget.name }),
        variant: 'success',
      });
      setDeleteTarget(null);
    } catch (caught) {
      setDeleteError(caught instanceof Error ? caught.message : tError('fallbackMessage'));
    } finally {
      setBusyId(null);
    }
  };

  const authLabel = (system: SystemRecord) => {
    // Same precedence as the prd SystemCard: template-shared auth wins over the
    // workspace's own (absent) auth config.
    if (system.has_shared_auth) return tSystemCard('auth_status.shared');
    if (
      system.auth_status === 'not_configured' ||
      system.auth_status === 'missing_credentials' ||
      system.auth_status === 'invalid'
    ) return tListing('missingCredentials');
    switch (system.auth_type) {
      case 'api_key': return tMcpAuth('api_key');
      case 'basic_auth': return tMcpAuth('basic_auth');
      case 'bearer_token': return tMcpAuth('bearer_token');
      case 'oauth2': return tMcpAuth('oauth2');
      case 'oauth2_cimd': return tMcpAuth('oauth2_cimd');
      case 'oauth2_client_credentials': return tMcpAuth('oauth2_client_credentials');
      case 'none': return tMcpAuth('none');
      default: return tMcpAuth('not_configured');
    }
  };

  if (isAccessLoading || workspaceScope === 'pending') {
    return <PageLoadingState className="min-h-[420px] bg-white" />;
  }
  if (!canOpenCurrent) {
    return <AccessDeniedModal isOpen onClose={() => undefined} />;
  }

  const columnCount = activeTab === 'systems' ? 6 : 6;

  return (
    <div className="flex min-h-0 w-0 min-w-full max-w-full flex-1 flex-col overflow-hidden bg-white">
      {showBanner && <ConnectorBanner />}

      <section data-testid={domId('sec', 'connector-management')} className="flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col gap-5 overflow-hidden px-0 pb-10 pt-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{tHome('title')}</h1>
          <p className="mt-1 text-sm text-slate-600">{tListing('subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard testId={domId('div', 'connector-metric-total-systems')} label={tListing('metrics.totalSystems')} value={totalSystemsResult.total ?? totalSystemsResult.systems?.length ?? 0} />
          <MetricCard testId={domId('div', 'connector-metric-active-systems')} label={tListing('metrics.activeSystems')} value={activeSystemsResult.total ?? activeSystemsResult.systems?.length ?? 0} />
          <MetricCard testId={domId('div', 'connector-metric-total-mcp')} label={tListing('metrics.totalMcp')} value={totalMcpsResult.total ?? totalMcpsResult.mcps?.length ?? 0} />
          <MetricCard testId={domId('div', 'connector-metric-active-mcp')} label={tListing('metrics.activeMcp')} value={activeMcpsResult.total ?? activeMcpsResult.mcps?.length ?? 0} />
        </div>

        <Tabs value={activeTab} onValueChange={value => switchTab(value as ConnectorTab)}>
          <TabsList className={CONNECTOR_DASHBOARD_TABS_LIST_CLASS}>
          {visibleTabs.map(tab => (
            <TabsTrigger
              key={tab}
              value={tab}
              data-testid={domId('btn', `connector-tab-${tab}`)}
              className={CONNECTOR_DASHBOARD_TABS_TRIGGER_CLASS}
            >
              {tHome(tab === 'systems' ? 'systemConnectorsSection' : 'mcpServersSection')}
            </TabsTrigger>
          ))}
          </TabsList>
        </Tabs>

        {/* Sized by its rows, not stretched to the window: a short list would
            otherwise leave a blank band above the pagination bar (AIS-2458). */}
        <div className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {mutationError && (
            <InlineStatus
              purpose="connector-mutation-error"
              status="error"
              className="mt-3"
              onDismiss={() => setMutationError(null)}
            >
              {mutationError}
            </InlineStatus>
          )}
          <div className="flex flex-col gap-3 border-b border-slate-200 p-4 xl:flex-row xl:items-center">
            <label className="relative min-w-0 flex-1 xl:max-w-sm">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <span className="sr-only">
                {tListing(activeTab === 'systems' ? 'searchSystems' : 'searchMcp')}
              </span>
              <Input
                data-testid={domId('txt', `connector-search-${activeTab}`)}
                value={search}
                onChange={event => setSearch(event.target.value)}
                placeholder={tListing(activeTab === 'systems' ? 'searchSystems' : 'searchMcp')}
                className="h-10 w-full rounded-lg bg-slate-50 pl-9 pr-3 shadow-none focus-visible:border-[#6F3D97] focus-visible:ring-[#6F3D97]/15"
              />
            </label>

            <div className="flex flex-wrap items-center gap-3 xl:ml-auto xl:justify-end">
              <FilterDropdown
                purpose={`connector-status-${activeTab}`}
                triggerTestId={domId('cbo', `connector-status-${activeTab}`)}
                value={statusFilter}
                onChange={value => setViewOption('status', value)}
                options={STATUS_FILTERS.map(value => ({
                  value,
                  label: value === 'all' ? tListing('allStatuses') : tStatus(value),
                }))}
                width="160px"
              />

              {activeTab === 'systems' && (
                <FilterDropdown
                  purpose="connector-auth-filter"
                  triggerTestId={domId('cbo', 'connector-auth-filter')}
                  value={authFilter}
                  onChange={value => setViewOption('auth', value)}
                  options={AUTH_FILTERS.map(value => ({
                    value,
                    label:
                      value === 'all'
                        ? tListing('allAuth')
                        : value === 'missing_credentials'
                          ? tListing('missingCredentials')
                          : tMcpAuth(value),
                  }))}
                  width="220px"
                />
              )}

              <FilterDropdown
                purpose={`connector-sort-${activeTab}`}
                triggerTestId={domId('cbo', `connector-sort-${activeTab}`)}
                value={sortValue}
                onChange={value => setViewOption('sort', value)}
                options={SORT_OPTIONS.map(option => ({
                  value: option.value,
                  label: tListing(option.labelKey),
                }))}
                width="220px"
              />

              <span className="text-xs font-medium text-slate-500">{tListing('results', { count: total })}</span>
              {((activeTab === 'systems' && canCreateSystem) ||
                (activeTab === 'mcp' && canCreateMcp)) && (
                <Button
                  data-testid={domId('btn', `connector-add-${activeTab}`)}
                  type="button"
                  variant="unstyled"
                  onClick={() => {
                    if (activeTab === 'systems') {
                      if (onAddSystem) onAddSystem();
                      else setAddSystemOpen(true);
                    } else if (onAddMcp) onAddMcp();
                    else setAddMcpOpen(true);
                  }}
                  className="inline-flex h-10 items-center gap-2 rounded-lg px-4 text-sm font-semibold text-white"
                  style={{
                    background: 'linear-gradient(90deg, #713D98 0%, #3E3D98 100%)',
                  }}
                >
                  <Plus className="h-4 w-4" />
                  {activeTab === 'systems' ? tHome('addConnector') : tListing('addMcp')}
                </Button>
              )}
            </div>
          </div>

          <div data-testid={domId('sec', 'connector-table-scroll')} className="min-h-0 w-full min-w-0 max-w-full overflow-auto overscroll-x-contain">
            <table className="w-full min-w-[920px] border-collapse">
              <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">{tListing(activeTab === 'systems' ? 'columns.connector' : 'columns.mcp')}</th>
                  <th className="px-4 py-3">{tStatus('label')}</th>
                  <th className="px-4 py-3">{activeTab === 'systems' ? tMcp('authLabel') : tMcp('manage.overview.transport')}</th>
                  <th className="px-4 py-3 text-right">{activeTab === 'systems' ? tSystem('tabs.actions') : tMcp('manage.overview.tools')}</th>
                  <th className="px-4 py-3 text-right">{tMcp('summaryCard.labels.lastUpdate')}</th>
                  <th className="w-14 px-4 py-3"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {loading && rows.length === 0 && <LoadingRows columns={columnCount} />}

                {!loading && error && (
                  <tr>
                    <td colSpan={columnCount} className="px-6 py-14 text-center">
                      <p className="text-sm font-medium text-rose-700">{activeTab === 'systems' ? tHome('errors.loadSystems') : tMcp('manage.notFound')}</p>
                      <Button
                        data-testid={domId('btn', `connector-retry-${activeTab}`)}
                        type="button"
                        variant="unstyled"
                        onClick={() => void (activeTab === 'systems' ? systemsResult.mutate() : mcpsResult.mutate())}
                        className="mt-3 rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"
                      >
                        {tError('actions.tryAgain')}
                      </Button>
                    </td>
                  </tr>
                )}

                {!loading && !error && rows.length === 0 && (
                  <tr>
                    <td colSpan={columnCount} className="px-6 py-14 text-center">
                      <p className="text-base font-semibold text-slate-800">{activeTab === 'systems' ? tHome('emptySystemTitle') : tMcp('list.emptyTitle')}</p>
                      <p className="mt-1 text-sm text-slate-500">{activeTab === 'systems' ? tHome('emptySystemSubtitle') : tMcp('list.emptyDescription')}</p>
                    </td>
                  </tr>
                )}

                {activeTab === 'systems' && systems.map(system => {
                  const lifecycle = deriveSystemLifecycle(system);
                  const disabled = busyId === system.system_id;
                  // prd SystemCard parity: every tag renders as a badge, and a
                  // read-only (foreign template) row exposes no manage actions.
                  const tags = Array.from(new Set(system.tags ?? []));
                  const canManageRow =
                    !system.read_only && (canEditSystem || canUpdateSystem || canDeleteSystem);
                  return (
                    <tr key={system.system_id} className="border-t border-slate-100 hover:bg-slate-50/70">
                      <td className="px-4 py-3">
                        <button
                          data-testid={domId('btn', `connector-open-${system.system_id}`)}
                          type="button"
                          onClick={() => navigateToSystem(system)}
                          disabled={!canOpenSystemDetails}
                          className="group flex max-w-md cursor-pointer items-center gap-3 text-left disabled:cursor-default"
                        >
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-violet-50 text-xs font-bold text-violet-700">
                            {connectorInitials(system.name, 'SC')}
                          </span>
                          <span className="min-w-0">
                            <span className="flex min-w-0 items-center gap-2">
                              <span
                                className={`truncate text-sm font-semibold text-slate-900 ${
                                  canOpenSystemDetails ? DETAIL_LINK_NAME_CLASS : ''
                                }`}
                              >
                                {system.name}
                              </span>
                              {tags.map(tag => (
                                <span
                                  key={tag}
                                  data-testid={domId('div', `connector-tag-${tag}-${system.system_id}`)}
                                  className="inline-flex shrink-0 items-center gap-1 rounded-full border border-[#E4DDF7] bg-[#F3EEFB] px-2 py-0.5 text-[10px] font-bold leading-3 text-[#3E3D98]"
                                >
                                  <LayoutTemplate className="h-3 w-3 text-[#713D98]" strokeWidth={2.5} />
                                  {tSystemCard.has(`tags.${tag}`) ? tSystemCard(`tags.${tag}`) : tag}
                                </span>
                              ))}
                            </span>
                            <span className="mt-0.5 block truncate text-xs text-slate-500">{system.description || system.system_type || '—'}</span>
                          </span>
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <LifecycleBadge className={lifecycle.className} dotClassName={lifecycle.dotClassName} label={tSystem(`lifecycle.${lifecycle.key}`)} />
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">{authLabel(system)}</td>
                      <td className="px-4 py-3 text-right text-sm text-slate-700">{tListing('configured', { count: system.api_spec_count ?? system.spec_count ?? 0 })}</td>
                      <td className="px-4 py-3 text-right text-sm text-slate-500">{formatUpdated(system.last_updated ?? system.last_updated_time, locale)}</td>
                      <td className="px-4 py-3 text-right">
                        {canManageRow && (
                          <ConnectorRowActions
                            buttonTestId={domId('btn', `connector-menu-${system.system_id}`)}
                            triggerTestId={domId('btn', `connector-menu-trigger-${system.system_id}`)}
                            disabled={disabled}
                            actions={[
                              ...(canEditSystem
                                ? [{
                                    testId: domId('btn', `connector-edit-${system.system_id}`),
                                    label: tSystem('actions.edit'),
                                    icon: Pencil,
                                    onSelect: () => navigateToSystem(system, true),
                                  }]
                                : []),
                              ...(canUpdateSystem
                                ? [{
                                    testId: domId('btn', `connector-lifecycle-${system.system_id}`),
                                    label: tSystem(isSystemInactive(system) ? 'lifecycle.activate' : 'lifecycle.deactivate'),
                                    icon: Power,
                                    onSelect: () => void handleSystemLifecycle(system),
                                  }]
                                : []),
                              ...(canDeleteSystem
                                ? [{
                                    testId: domId('btn', `connector-delete-${system.system_id}`),
                                    label: tSystem('actions.delete'),
                                    icon: Trash2,
                                    onSelect: () => {
                                      setDeleteError(null);
                                      setDeleteTarget({ kind: 'system', id: system.system_id, name: system.name });
                                    },
                                    destructive: true,
                                    separatorBefore: canEditSystem || canUpdateSystem,
                                  }]
                                : []),
                            ]}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}

                {activeTab === 'mcp' && mcps.map(server => {
                  const lifecycle = deriveMcpLifecycle(server);
                  const disabled = busyId === server.mcp_id;
                  const transport = server.config?.detected_transport || server.config?.transport || tMcpAuth('not_configured');
                  return (
                    <tr key={server.mcp_id} className="border-t border-slate-100 hover:bg-slate-50/70">
                      <td className="px-4 py-3">
                        <button data-testid={domId('btn', `mcp-open-${server.mcp_id}`)} type="button" onClick={() => navigateToMcp(server)} className="group flex max-w-md cursor-pointer items-center gap-3 text-left">
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal-50 text-xs font-bold text-teal-700">
                            {connectorInitials(server.name, 'MC')}
                          </span>
                          <span className="min-w-0">
                            <span className={`block truncate text-sm font-semibold text-slate-900 ${DETAIL_LINK_NAME_CLASS}`}>{server.name || tMcp('untitled')}</span>
                            <span className="mt-0.5 block truncate text-xs text-slate-500">{server.description || server.config?.server_info?.description || server.config?.endpoint_url || '—'}</span>
                          </span>
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <LifecycleBadge className={lifecycle.className} dotClassName={lifecycle.dotClassName} label={tMcp(`lifecycle.${lifecycle.key}`)} />
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-700">{transport.replaceAll('_', ' ')}</td>
                      <td className="px-4 py-3 text-right text-sm text-slate-700">{tListing('discovered', { count: server.config?.tool_count ?? 0 })}</td>
                      <td className="px-4 py-3 text-right text-sm text-slate-500">{formatUpdated(server.last_updated_time, locale)}</td>
                      <td className="px-4 py-3 text-right">
                        <ConnectorRowActions
                          buttonTestId={domId('btn', `mcp-menu-${server.mcp_id}`)}
                          triggerTestId={domId('btn', `mcp-menu-trigger-${server.mcp_id}`)}
                          disabled={disabled}
                          actions={[
                            ...(canUpdateMcp
                              ? [{
                                  testId: domId('btn', `mcp-edit-${server.mcp_id}`),
                                  label: tSystem('actions.edit'),
                                  icon: Pencil,
                                  onSelect: () => navigateToMcp(server, true),
                                }]
                              : []),
                            ...(canManageMcpStatus
                              ? [{
                                  testId: domId('btn', `mcp-lifecycle-${server.mcp_id}`),
                                  label: tMcp(isInactive(server) ? 'lifecycle.activate' : 'lifecycle.deactivate'),
                                  icon: Power,
                                  onSelect: () => void handleMcpLifecycle(server),
                                }]
                              : []),
                            ...(canDeleteMcp
                              ? [{
                                  testId: domId('btn', `mcp-delete-${server.mcp_id}`),
                                  label: tMcp('manage.delete'),
                                  icon: Trash2,
                                  onSelect: () => {
                                    setDeleteError(null);
                                    setDeleteTarget({ kind: 'mcp', id: server.mcp_id, name: server.name || tMcp('untitled') });
                                  },
                                  destructive: true,
                                  separatorBefore: canUpdateMcp || canManageMcpStatus,
                                }]
                              : []),
                          ]}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {total > 0 && (
            <div className="shrink-0 border-t border-slate-200 bg-white px-4 pb-4">
              <Pagination
                purpose={`connector-revamp-${activeTab}`}
                page={page}
                pageSize={pageSize}
                total={total}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
              />
            </div>
          )}
        </div>
      </section>

      {renderDialogs && canCreateSystem && (
        <AddConnectorModal
          open={addSystemOpen}
          onOpenChange={setAddSystemOpen}
          initialStep="profile"
          dismissMode="guard"
        />
      )}
      {renderDialogs && canCreateMcp && (
        <AddMcpModal
          open={addMcpOpen}
          onClose={() => setAddMcpOpen(false)}
          onCreated={mcpId => {
            setAddMcpOpen(false);
            void refreshMcps();
            void pushWithMinimumDelay(
              router,
              buildWorkspaceAwarePath(withLocalePath(locale, `/mcp/${mcpId}`)),
              { skipUnsavedCheck: true },
            );
          }}
        />
      )}
      <ConfirmDeleteModal
        open={Boolean(deleteTarget)}
        title={deleteTarget ? tListing('deleteTitle', { name: deleteTarget.name }) : undefined}
        message={deleteTarget?.kind === 'mcp' ? tMcp('manage.deleteConfirm.message') : tDelete('message')}
        onClose={() => {
          if (!busyId) {
            setDeleteTarget(null);
            setDeleteError(null);
          }
        }}
        onConfirm={() => void confirmDelete()}
        deleting={Boolean(deleteTarget && busyId === deleteTarget.id)}
        error={deleteError}
        onDismissError={() => setDeleteError(null)}
      />
      <Toaster toasts={toasts} onToastClose={removeToast} />
    </div>
  );
}
